import clsx from 'clsx';
import { Suspense } from "react";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  HiHome,
  HiUpload,
  HiLocationMarker,
  HiTag,
  HiViewGrid, // Layout
  HiMap, // Map Style
  HiAdjustments, // Trace Style (alternative icon)
  HiChartBar, // Profile (alternative icon)
} from "react-icons/hi";

import Spinner from "../components/Spinner";
import ErrorBoundary from "../components/ErrorBoundary";
import AnimationLayout from "../components/AnimationLayout";

// Pages
import Overview from "../pages/Overview.tsx";
import Activities from "../pages/Activities.tsx";
import Points from "../pages/Points.tsx";
import Labels from "../pages/Labels.tsx";
// --- New Pages ---
import Layout from "../pages/Layout.tsx";
import MapStyle from "../pages/MapStyle.tsx";
import Trace from "../pages/Trace.tsx";
import Profile from "../pages/Profile.tsx";
import Cart from "../pages/Cart.tsx";
import Checkout from "../pages/Checkout.tsx";
import Success from "../pages/Success.tsx";
import NotFound from "../components/NotFound"; // Import de la page NotFound
import { EditorPreviewRef } from "./EditorPreview"; // Import EditorPreviewRef

interface SidebarProps {
  isSidebarOpen: boolean;
  editorPreviewRef: React.RefObject<EditorPreviewRef | null>; // Pass ref if needed by child pages
  showBanner: boolean; // Add showBanner prop
}

const Sidebar = ({ isSidebarOpen, editorPreviewRef, showBanner }: SidebarProps) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(isSidebarOpen);
  const location = useLocation(); // To handle active state for '/' and '/overview'
  const { t, i18n } = useTranslation(); // Initialize useTranslation et accès à i18n

  // Use effect to handle sidebar visibility for animation
  useEffect(() => {
    if (isSidebarOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isSidebarOpen]);

  // Synchronise Redux avec la langue i18next au montage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedLang = window.localStorage.getItem('i18nextLng');
      if (storedLang && storedLang !== i18n.language) {
        i18n.changeLanguage(storedLang);
      }
    }
  }, []);

  const navItems = [
    { path: "/overview", icon: HiHome, labelKey: "sidebar.home" },
    { path: "/activities", icon: HiUpload, labelKey: "sidebar.activities" },
    { path: "/points", icon: HiLocationMarker, labelKey: "sidebar.points" },
    // { path: "/templates", icon: HiTemplate, labelKey: "sidebar.templates" }, // CACHÉ
    { path: "/labels", icon: HiTag, labelKey: "sidebar.labels" },
    { path: "/layout", icon: HiViewGrid, labelKey: "sidebar.layout" },
    { path: "/map", icon: HiMap, labelKey: "sidebar.map" },
    { path: "/trace", icon: HiAdjustments, labelKey: "sidebar.trace" },
    { path: "/profile", icon: HiChartBar, labelKey: "sidebar.profile" },
    // Ajout du bouton "Voir le poster" en dernier
    { path: "#seePoster", icon: () => <span style={{fontSize: '1.5em'}}>👁️</span>, labelKey: "sidebar.seePoster", isPoster: true },
  ];

  // Calcul dynamique de la hauteur disponible pour la nav (header + éventuelle bannière)
  const headerHeight = 0;
  const bannerHeight = showBanner ? 0 : 0; // 56px = hauteur estimée de la bannière promo
  const navHeight = `calc(100vh - ${headerHeight + bannerHeight}px)`;

  return (
    <aside
      ref={sidebarRef}
      className={`bg-[#222] w-full max-w-[700px] min-w-0 pl-0 sm:pl-0 shadow-md flex flex-row gap-0 min-h-screen ${showBanner ? 'h-[calc(100vh-0px)]' : 'h-[calc(100vh-1px)]'} transition-all duration-300 ease-in-out sm:w-[700px] sm:max-w-[700px] md:w-[700px] md:max-w-[700px] lg:w-[700px] lg:max-w-[700px] ${
        isSidebarOpen
          ? "translate-x-0 opacity-100"
          : "-translate-x-full opacity-0"
      } ${isVisible ? "block" : "absolute"}`} // Adjust height based on banner visibility
    >
      {/* Navigation Icons */}
      <div
        className="w-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#333] scrollbar-track-[#222] flex flex-col justify-start items-stretch"
        style={{ maxHeight: navHeight }}
      >
        <ul className="flex flex-col gap-3">
          {navItems.filter(item => !item.isPoster).map(item => (
            <li key={item.path} className={'h-12 md:h-16 flex-1 md:flex-none min-h-0'}>
              <NavLink
                to={item.path}
                data-tooltip-id={`${t(item.labelKey).toLowerCase()}-tooltip`}
                data-tooltip-content={t(item.labelKey)}
                className={({ isActive }) =>
                  clsx(
                    'h-full px-2 py-1.5 font-semibold flex flex-col items-center justify-center gap-1 cursor-pointer text-center hover:bg-[#2A2A2A] h-[64px]',
                    (isActive && item.path !== "/overview") || (item.path === "/overview" && (isActive || location.pathname === "/"))
                      ? 'bg-[#333333]'
                      : ''
                  )
                }
              >
                <item.icon className="w-5 h-5 text-white mb-0.5 scale-110" />
                <span className="text-[13px] text-white pt-1 leading-tight">
                  {t(item.labelKey)}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {/* Page Content Area */}
      <div className="border-l border-[#333] flex-col items-center py-8 px-6 pb-52 md:pb-24 overflow-y-auto scrollbar-thin scrollbar-thumb-[#333] scrollbar-track-[#222] max-h-full w-full max-w-lg"> {/* Centrage du contenu + largeur élargie */}
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <Spinner />
              </div>
            }
          >
            <Routes>
              <Route element={<AnimationLayout />}>
                {/* Existing Routes */}
                <Route path="/" element={<Overview editorPreviewRef={editorPreviewRef} />} />
                <Route path="/overview" element={<Overview editorPreviewRef={editorPreviewRef} />} />
                <Route path="/activities" element={<Activities editorPreviewRef={editorPreviewRef} />} />
                <Route path="/points" element={<Points editorPreviewRef={editorPreviewRef}/>}/>
                <Route path="/labels" element={<Labels editorPreviewRef={editorPreviewRef} />} />
                {/* <Route path="/templates" element={<TemplatePage />} /> */} {/* Route cachée */}
                {/* --- New Routes --- */}
                <Route path="/layout" element={<Layout editorPreviewRef={editorPreviewRef} />} />
                <Route path="/map" element={<MapStyle editorPreviewRef={editorPreviewRef} />} />
                <Route path="/trace" element={<Trace editorPreviewRef={editorPreviewRef} />} />
                <Route path="/profile" element={<Profile editorPreviewRef={editorPreviewRef} />} />
              </Route>
               {/* Routes outside the main editor sidebar animation */}
               <Route path="/cart" element={<Cart />} />
               <Route path="/checkout" element={<Checkout />} />
               <Route path="/commande/succes" element={<Success />} />
               {/* Route 404 */}
               <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* Tooltips */}
      {navItems.map(item => (
         <Tooltip
            key={`${t(item.labelKey).toLowerCase()}-tooltip`}
            id={`${t(item.labelKey).toLowerCase()}-tooltip`}
            place="right"
            style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }} // Ensure high z-index
            opacity={1}
         />
      ))}
    </aside>
  );
};

export default Sidebar;